import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, effect, input, output, viewChild } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { MinimumSlideInformation } from '@pcp/core/interfaces';
import { isDynamicExtensionSlide, isNotNullOrUndefined } from '@pcp/core/util';
import { IsDynamicExtensionSlidePipe, IsVideoSlidePipe } from 'shared/classroom/preview-screen/ui-input-pipes';
import { LineBreaksPipe } from 'shared/ui/directives';

const THUMB_SMALL_WIDTH = 245; //px, for 4K screen
const THUMB_SMALL_MARGIN_LEFT = 9; //px, for 4K screen
const LARGE_SCREEN_BREAKPOINT = 1921; //px

/**
 * Extension slides row of the beamer nav: [‹] [thumbs] [›] — three cells of the layout's grid (`display: contents`).
 * Pure UI: the layout handles the outputs.
 * Styles: libs/styles/slide-sync/beamer-navigation/_nav-parts.scss (global, beamer themes).
 */
@Component({
    selector: 'pcp-nav-extension-slides-row',
    templateUrl: './nav-extension-slides-row.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIconButton,
        MatIcon,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        LineBreaksPipe,
        IsVideoSlidePipe,
        IsDynamicExtensionSlidePipe,
        TranslocoPipe,
    ],
})
export class NavExtensionSlidesRowComponent {
    readonly slides = input<MinimumSlideInformation[] | undefined>(undefined);
    /** Extension slide (0…n-1) shown first in the preview. */
    readonly previewIdx = input<number | undefined>(undefined);
    /** Extension slide that is active on the beamer. */
    readonly currentSlideIdx = input<number | undefined>(undefined);
    /** The extension preview is open and has slides. */
    readonly visible = input(false);
    readonly previousEnabled = input<boolean | undefined>(undefined);
    readonly nextEnabled = input<boolean | undefined>(undefined);
    /** Thumbs per row (class `length{n}` sets the viewport width). */
    readonly visibleCount = input.required<number>();

    readonly slideClicked = output<MinimumSlideInformation>();
    readonly previousClicked = output<void>();
    readonly nextClicked = output<void>();
    /** Mouse wheel: the new first visible extension slide. */
    readonly previewIdxChange = output<number>();

    private readonly scrollContainer = viewChild<CdkVirtualScrollViewport>('scrollContainer');

    /** Thumb width incl. margin, in px (half of the 4K value below the large-screen breakpoint). */
    protected readonly thumbWidth =
        window.innerWidth >= LARGE_SCREEN_BREAKPOINT
            ? THUMB_SMALL_WIDTH + THUMB_SMALL_MARGIN_LEFT
            : (THUMB_SMALL_WIDTH + THUMB_SMALL_MARGIN_LEFT) / 2;

    constructor() {
        // When the row is shown, or the preview index changes ("‹" / "›" / mouse wheel): scroll to the preview index.
        // Only `visible()` is checked: opening the extension preview hides the basic preview, so the two are
        // never shown together (#33667). setTimeout keeps the old timing: the viewport is rendered and measured first.
        effect(() => {
            const viewport = this.scrollContainer();
            const index = this.previewIdx() ?? 0;
            if (viewport && this.visible()) {
                setTimeout(() => viewport.scrollToIndex(index, 'smooth'), 0);
            }
        });
    }

    protected readonly isTextThumb = (slide: MinimumSlideInformation) =>
        isNotNullOrUndefined(slide) && isDynamicExtensionSlide(slide);

    protected onScrollWheel(event: WheelEvent) {
        try {
            // add try...catch around stopPropagation because it's allowed only if the event listener is "passive==false"
            event.stopPropagation();
        } catch {}
        if (!this.slides()?.length) {
            return;
        }

        const scrollStep: number = event.deltaMode === 1 ? event.deltaY * 34 : event.deltaY; //cross-browser scroll step
        const offsetLeft = this.scrollContainer()?.measureScrollOffset('left') || 0;
        this.previewIdxChange.emit(Math.round((offsetLeft + scrollStep * 2) / this.thumbWidth));
    }
}
